import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type AnchorHTMLAttributes,
  type ReactNode,
} from "react";
import {
  Link,
  Redirect,
  Route,
  Router,
  Switch,
  useLocation as useWouterLocation,
  useParams,
  useSearchParams,
  type AroundNavHandler,
} from "wouter";

type LocationSnapshot = {
  pathname: string;
  search: string;
};

type BlockerPredicate = (transition: {
  currentLocation: LocationSnapshot;
  nextLocation: LocationSnapshot;
}) => boolean;

type BlockerRegistration = {
  shouldBlock: BlockerPredicate;
  block: (proceed: () => void) => void;
};

type RouteObject = {
  path?: string;
  element: ReactNode;
  children?: RouteObject[];
};

export type BrowserRouter = {
  routes: RouteObject[];
};

const BlockerRegistryContext = createContext<Set<BlockerRegistration> | null>(null);
const OutletElementContext = createContext<ReactNode>(null);
const OutletValueContext = createContext<unknown>(undefined);

function browserLocation(): LocationSnapshot {
  return {
    pathname: window.location.pathname,
    search: window.location.search,
  };
}

function toLocation(to: string): LocationSnapshot {
  const next = new URL(to, window.location.href);
  return { pathname: next.pathname, search: next.search };
}

export function createBrowserRouter(routes: RouteObject[]): BrowserRouter {
  return { routes };
}

function RouteTree({ routes }: { routes: RouteObject[] }) {
  const shell = routes.find((route) => route.children);
  const fallback = routes.find((route) => route.path === "*");

  if (!shell?.children) {
    return (
      <Switch>
        {routes.map((route, index) => (
          <Route key={route.path ?? index} path={route.path}>
            {route.element}
          </Route>
        ))}
      </Switch>
    );
  }

  return (
    <Switch>
      {routes.filter((route) => route.path && route.path !== "*" && !route.children).map((route, index) => (
        <Route key={route.path ?? index} path={route.path}>
          {route.element}
        </Route>
      ))}
      {shell.children.map((route, index) => (
        <Route key={route.path ?? index} path={route.path}>
          <OutletElementContext.Provider value={route.element}>
            {shell.element}
          </OutletElementContext.Provider>
        </Route>
      ))}
      <Route>{fallback?.element ?? null}</Route>
    </Switch>
  );
}

export function RouterProvider({ router }: { router: BrowserRouter }) {
  const blockers = useMemo(() => new Set<BlockerRegistration>(), []);
  const aroundNav = useCallback<AroundNavHandler>(
    (navigate, to, options) => {
      const transition = {
        currentLocation: browserLocation(),
        nextLocation: toLocation(to),
      };
      const blocker = Array.from(blockers).find((item) => item.shouldBlock(transition));

      if (blocker) {
        blocker.block(() => navigate(to, options));
        return;
      }

      navigate(to, options);
    },
    [blockers],
  );

  return (
    <BlockerRegistryContext.Provider value={blockers}>
      <Router aroundNav={aroundNav}>
        <RouteTree routes={router.routes} />
      </Router>
    </BlockerRegistryContext.Provider>
  );
}

export function Outlet({ context }: { context?: unknown }) {
  const element = useContext(OutletElementContext);
  return <OutletValueContext.Provider value={context}>{element}</OutletValueContext.Provider>;
}

export function useOutletContext<T = unknown>(): T {
  return useContext(OutletValueContext) as T;
}

export function useLocation(): LocationSnapshot {
  const [pathname] = useWouterLocation();
  return { pathname, search: window.location.search };
}

type NavLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "className" | "href"> & {
  to: string;
  className?: string;
  end?: boolean;
  replace?: boolean;
};

export function NavLink({ className, end = false, to, replace, ...props }: NavLinkProps) {
  const [pathname] = useWouterLocation();
  const targetPath = to.split(/[?#]/, 1)[0] || pathname;
  const active = end
    ? pathname === targetPath
    : pathname === targetPath || pathname.startsWith(`${targetPath}/`);

  return (
    <Link
      {...props}
      to={to}
      replace={replace}
      className={[className, active ? "active" : ""].filter(Boolean).join(" ")}
      aria-current={active ? "page" : props["aria-current"]}
    />
  );
}

export function Navigate({ to, replace = false }: { to: string; replace?: boolean }) {
  return <Redirect to={to} replace={replace} />;
}

export function useBlocker(shouldBlock: BlockerPredicate) {
  const registry = useContext(BlockerRegistryContext);
  const predicateRef = useRef(shouldBlock);
  const pendingRef = useRef<(() => void) | null>(null);
  const [state, setState] = useState<"unblocked" | "blocked">("unblocked");
  predicateRef.current = shouldBlock;

  const registration = useMemo<BlockerRegistration>(
    () => ({
      shouldBlock: (transition) => predicateRef.current(transition),
      block: (proceed) => {
        pendingRef.current = proceed;
        setState("blocked");
      },
    }),
    [],
  );

  useEffect(() => {
    if (!registry) return;
    registry.add(registration);
    return () => {
      registry.delete(registration);
    };
  }, [registration, registry]);

  const reset = useCallback(() => {
    pendingRef.current = null;
    setState("unblocked");
  }, []);

  const proceed = useCallback(() => {
    const navigate = pendingRef.current;
    pendingRef.current = null;
    setState("unblocked");
    navigate?.();
  }, []);

  return { state, reset, proceed };
}

export { Link, useParams, useSearchParams };
